// Libraries
import React from 'react';

// Supermove
import {Space} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {ProjectModel, ReferralSource} from '@supermove/models';

// App
import DrawerWithAction from '@shared/design/components/Drawer/DrawerWithAction';
import DropdownInput from '@shared/design/components/DropdownInput';
import FieldInput from '@shared/design/components/Field/FieldInput';
import ProjectReferralInformationForm, {
  ProjectReferralInformationFormType,
} from '@shared/modules/Project/forms/ProjectReferralInformationForm';
import useUpdateProjectReferralInformationMutation from '@shared/modules/Project/hooks/useUpdateProjectReferralInformationMutation';

const EditProjectReferralInformationDrawerFields = ({
  form,
  field,
  project,
}: {
  form: ProjectReferralInformationFormType;
  field: string;
  project: ProjectModel;
}) => {
  const {organization} = project;
  const {isEnabledProjectReferralSourceRequired} = organization.features;
  const {referralSources} = organization.settings;

  return (
    <React.Fragment>
      <FieldInput
        {...form}
        index={0}
        component={DropdownInput}
        name={`${field}.referralSource`}
        label={'How did you hear about us?'}
        isRequired={isEnabledProjectReferralSourceRequired}
        input={{
          placeholder: 'Select referral source',
          options: ReferralSource.getDropdownOptions({referralSources}),
          setFieldValue: form.setFieldValue,
          onChangeValue: (value: string, option: unknown, previousValue: string) => {
            // When the referralSource is updated, we clear the referralDetails.
            if (previousValue !== value) {
              form.setFieldValue(`${field}.referralDetails`, '');
            }
          },
          style: {flex: 1},
        }}
        style={{flex: 1}}
      />
      <Space height={16} />
      <FieldInput
        {...form}
        index={1}
        name={`${field}.referralDetails`}
        label={'Referral Details'}
        input={{
          placeholder: 'Enter referral details',
        }}
        style={{flex: 1}}
      />
      <Space height={16} />
    </React.Fragment>
  );
};

const EditProjectReferralInformationDrawer = ({
  project,
  isOpen,
  handleClose,
  refetch,
}: {
  project: ProjectModel;
  isOpen: boolean;
  handleClose: () => void;
  refetch: () => void;
}) => {
  const projectReferralInformationForm = ProjectReferralInformationForm.edit(project);

  const {form, handleSubmit, submitting} = useUpdateProjectReferralInformationMutation({
    projectReferralInformationForm,
    onSuccess: () => {
      handleClose();
      refetch();
    },
    onError: (errors) => console.log({errors}),
  });

  return (
    <DrawerWithAction
      isOpen={isOpen}
      handleClose={handleClose}
      headerText={'Edit Referral'}
      isSubmitting={submitting}
      handleSubmit={handleSubmit}
    >
      <EditProjectReferralInformationDrawerFields
        form={form}
        field={'projectReferralInformationForm'}
        project={project}
      />
    </DrawerWithAction>
  );
};

// --------------------------------------------------
// Data
// --------------------------------------------------
EditProjectReferralInformationDrawer.fragment = gql`
  ${ProjectReferralInformationForm.edit.fragment}
  ${ReferralSource.getDropdownOptions.fragment}

  fragment EditProjectReferralInformationDrawer on Project {
    id
    organization {
      id
      settings {
        id
        referralSources {
          ...ReferralSource_getDropdownOptions
        }
      }
      features {
        isEnabledProjectReferralSourceRequired: isEnabled(
          feature: "PROJECT_REFERRAL_SOURCE_REQUIRED"
        )
      }
    }
    ...ProjectReferralInformationForm_edit
  }
`;

export default EditProjectReferralInformationDrawer;
